import React, {useEffect, useState} from 'react';
import {fetchAllAppointments} from '@shared/services/AppointmentsServices';
import {IAppointment} from '@shared/services/service-mappers/AppointmentMapper';
import {
  IModuleTicketFields,
  IPriorAuthTicketDetails
} from '@shared/services/service-mappers/WorkflowMapper';
import {appointmentsDateTimeFormat} from '@shared/utils/DateTimeUtils';
import HelperUtils from '@shared/utils/HelperUtils';
import {useSnackbar} from 'notistack';
import {Controller, UseFormMethods} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {State} from '@shared/store/reducers/Index';
import {FormField} from '@components/system/FormField/FormField';
import {filterFutureAppointments, getClinicTimeZone} from '../helpers/utils';
import './PriorAuthorizationTicketDetailsFields.scss';
import {
  AppPermissions,
  PRIOR_AUTH_ADDITIONAL_FIELDS,
  SMART_CODER_RELATED_TYPES,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import GenerateCPTCodeDialog, {
  IGenerateCPTCodeDialogProps
} from '../../SmartCoder/GenerateCPTCodeDialog/GenerateCPTCodeDialog';
import {updateTicket} from '@shared/services/WorkflowServices';
import {useSmartCoderPermission} from '../../SmartCoder/hooks/useSmartCoderPermission';
import {ListItemText} from '@material-ui/core';
import useIsGenerateProcedureEnabled from '../hooks/useIsGenerateProcedureEnabled';

interface IPriorAuthorizationTicketDetailsFieldsProps {
  isEditing: boolean;
  methods: UseFormMethods;
  clinicGroupId: number;
  ticketDetails: IPriorAuthTicketDetails | null;
  refreshTicketDetails: () => void;
  clinicProvidersList: any[];
  clinicPayersList: any[];
  serviceLocations: any[];
  customFields: IModuleTicketFields[];
  workFlow?: any;
}
/* eslint-disable camelcase */
export interface IPayer {
  label: string;
  value: number;
}
const PriorAuthorizationTicketDetailsFields = (
  props: IPriorAuthorizationTicketDetailsFieldsProps
): JSX.Element => {
  const {
    methods,
    isEditing,
    clinicGroupId,
    ticketDetails,
    refreshTicketDetails,
    clinicPayersList,
    serviceLocations,
    clinicProvidersList,
    customFields,
    workFlow
  } = props;

  const {userPermissions, groupId} = useSelector((state: any) => state.auth);
  const appointmentsEnabled: boolean =
    userPermissions?.[groupId]?.['group']?.[
      AppPermissions.MENU_PERMISSIONS.APPOINTMENTS
    ];
  const {t} = useTranslation();
  const {selectedClinicId} = useSelector((state: State) => state.clinics);
  const clinicId = selectedClinicId?.[0];
  const {enqueueSnackbar} = useSnackbar();

  const [fieldOptions, setFieldOptions] = useState<{
    patientAppointmentsList: IAppointment[];
  }>({
    patientAppointmentsList: []
  });
  const [workFlowStatusCode, setWorkFlowStatusCode] = useState(null);
  // const [customFieldsBasedOnStatus, setCustomFieldsBasedOnStatus] = useState([...customFields])

  const [showCPTCodeGenerator, setShowCPTCodeGenerator] = useState(false);

  const {
    patient: selectedPatient,
    appointment_id: selectedAppointmentId
  } = methods.watch(['patient', 'appointment_id']);

  const getAllAppointmentHistory = async (patientId: number): Promise<void> => {
    if (!clinicId || !patientId) {
      setFieldOptions(prev => ({
        ...prev,
        patientAppointmentsList: []
      }));
      return;
    }
    try {
      if (appointmentsEnabled) {
        const res = await fetchAllAppointments({
          clinic_group_id: clinicGroupId,
          params: {
            clinic_id: clinicId,
            patient_id: patientId
          }
        });
        if (Array.isArray(res?.data?.rows)) {
          setFieldOptions(prev => ({
            ...prev,
            patientAppointmentsList: res.data.rows
          }));
        } else {
          setFieldOptions(prev => ({
            ...prev,
            patientAppointmentsList: []
          }));
        }
      }
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || t('PATIENTS.GET_PATIENT_APPOINTMENT_DETAILS_ERROR'),
        SnackBarConfig('e')
      );
    }
  };

  // Function to get the 'code' from 'workFlow' based on 'workflow_status_id' from 'ticketDetails'

  useEffect(() => {
    // eslint-disable-next-line no-shadow
    const getWorkflowCode = (ticketDetails: any, workFlow: any) => {
      let workFlowCode = null;
      if (
        ticketDetails &&
        Object.keys(ticketDetails).length > 0 &&
        ticketDetails.workflow_status_id
      ) {
        // eslint-disable-next-line camelcase
        const {workflow_status_id} = ticketDetails; // Extracting workflow_status_id from ticketDetails

        // Find the matching workflow object using the workflow_status_id
        // eslint-disable-next-line camelcase
        if (workFlow && workFlow?.status && workFlow?.status?.length > 0) {
          const matchingWorkflow = workFlow.status.find(
            // eslint-disable-next-line camelcase
            (item: {id: any}) => item.id === workflow_status_id
          );
          workFlowCode = matchingWorkflow.code ? matchingWorkflow.code : null;
        }
      }
      setWorkFlowStatusCode(workFlowCode);
      return workFlowCode;
    };
    getWorkflowCode(ticketDetails, workFlow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketDetails]);
  // Update the value in the react hook form
  useEffect(() => {
    if (ticketDetails) {
      methods.setValue('prior_auth_id', ticketDetails.prior_auth?.id);
      methods.setValue(
        'appointment_id',
        ticketDetails.prior_auth?.appointment_id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketDetails]);

  useEffect(() => {
    methods.setValue('appointment_id', null);
    if (selectedPatient?.id) {
      getAllAppointmentHistory(selectedPatient.id);
    } else {
      setFieldOptions(prev => ({
        ...prev,
        patientAppointmentsList: []
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicGroupId, clinicId, selectedPatient?.id]);

  useEffect(() => {
    if (ticketDetails?.patient_id) {
      getAllAppointmentHistory(ticketDetails?.patient_id);
      methods.setValue(
        'appointment_id',
        ticketDetails.prior_auth?.appointment_id
      );
    } else {
      setFieldOptions(prev => ({
        ...prev,
        patientAppointmentsList: []
      }));
      methods.setValue('appointment_id', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicGroupId, clinicId, ticketDetails?.patient_id]);

  const clinicList = useSelector((state: State) => state.clinics.list || []);

  // if the appointment changes, update the date_of_service field to the date of appointment
  const updateDateOfServiceOnAppointmentChanges = (apptId: number) => {
    if (apptId) {
      const selectedAppointment = fieldOptions.patientAppointmentsList.find(
        item => item.id === apptId
      );
      if (selectedAppointment) {
        // splitting it to get only date part and setting it to date_of_service field
        methods.setValue(
          'date_of_service',
          appointmentsDateTimeFormat(
            selectedAppointment?.appointment_at,
            getClinicTimeZone(clinicList, clinicId)
          )?.split(' ')?.[0] ?? null
        );
      }
    }
  };

  // Filter out only future appointments
  const patientAppointmentsOptionsList = filterFutureAppointments(
    fieldOptions.patientAppointmentsList
  ).map(appointment => {
    return {
      label: appointment.type,
      secondary_label: appointmentsDateTimeFormat(
        appointment?.appointment_at,
        getClinicTimeZone(clinicList, clinicId)
      ),
      value: appointment.id
    };
  });

  // Need to manually insert the option for the current appointment if it is not a future appointment, so that the option could be selected as default
  if (ticketDetails?.prior_auth?.appointment_id) {
    const isCurrentAppointmentPresent = patientAppointmentsOptionsList.find(
      apptOption =>
        apptOption.value === ticketDetails?.prior_auth?.appointment_id
    );
    const currentAppointmentDetails = fieldOptions.patientAppointmentsList.find(
      appt => appt.id === ticketDetails?.prior_auth?.appointment_id
    );
    if (!isCurrentAppointmentPresent && currentAppointmentDetails) {
      patientAppointmentsOptionsList.unshift({
        label: currentAppointmentDetails.type,
        secondary_label: appointmentsDateTimeFormat(
          currentAppointmentDetails?.appointment_at,
          getClinicTimeZone(clinicList, clinicId)
        ),
        value: currentAppointmentDetails.id
      });
    }
  }

  const providersOptionList = clinicProvidersList.map(provider => {
    return {
      label: HelperUtils.commaSeparatedFullName(
        provider.first_name,
        provider.last_name,
        provider.middle_name
      ),
      secondary_label: provider.npi,
      value: provider.id
    };
  });

  const payerOptionsList = clinicPayersList.map(payer => {
    return {
      label: payer.payer_name,
      value: payer.id
    };
  });
  const serviceLocationOptionsList = serviceLocations.map(i => {
    return {
      label: i.location_name,
      value: i.id
    };
  });

  const fetchPayersWithMatchingText = async (
    searchText: string
  ): Promise<IPayer[] | undefined> => {
    try {
      return payerOptionsList.filter(payer =>
        payer.label.toLowerCase().includes(searchText.toLowerCase())
      );
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };
  const excludedFieldsByStatus = {
    approval: [
      PRIOR_AUTH_ADDITIONAL_FIELDS.APPROVED_CODES,
      PRIOR_AUTH_ADDITIONAL_FIELDS.DENIED_CODES
    ],
    denied: [
      PRIOR_AUTH_ADDITIONAL_FIELDS.AUTHORIZATION_ID,
      PRIOR_AUTH_ADDITIONAL_FIELDS.APPROVED_CODES,
      PRIOR_AUTH_ADDITIONAL_FIELDS.DENIED_CODES,
      PRIOR_AUTH_ADDITIONAL_FIELDS.START_DATE,
      PRIOR_AUTH_ADDITIONAL_FIELDS.END_DATE
    ],
    'partial-approved': [
      PRIOR_AUTH_ADDITIONAL_FIELDS.AUTHORIZATION_ID,
      PRIOR_AUTH_ADDITIONAL_FIELDS.START_DATE,
      PRIOR_AUTH_ADDITIONAL_FIELDS.END_DATE
    ],
    'pending-for-additional-info': [
      PRIOR_AUTH_ADDITIONAL_FIELDS.AUTHORIZATION_ID,
      PRIOR_AUTH_ADDITIONAL_FIELDS.START_DATE,
      PRIOR_AUTH_ADDITIONAL_FIELDS.END_DATE,
      PRIOR_AUTH_ADDITIONAL_FIELDS.APPROVED_CODES,
      PRIOR_AUTH_ADDITIONAL_FIELDS.DENIED_CODES
    ]
  };

  // This will handle all the cases including the default (workflow status code not matching any of the above).
  const defaultExcludedFields = [
    PRIOR_AUTH_ADDITIONAL_FIELDS.AUTHORIZATION_ID,
    PRIOR_AUTH_ADDITIONAL_FIELDS.ADDITIONAL_INFO_NOTES,
    PRIOR_AUTH_ADDITIONAL_FIELDS.APPROVED_CODES,
    PRIOR_AUTH_ADDITIONAL_FIELDS.DENIED_CODES,
    PRIOR_AUTH_ADDITIONAL_FIELDS.START_DATE,
    PRIOR_AUTH_ADDITIONAL_FIELDS.END_DATE
  ];

  const hasSmartCoderPermission = useSmartCoderPermission({
    objectFunction: 'GENERATE_CODE'
  });

  const ticketInputFields = customFields
    .map(field => {
      const inputField: Record<string, any> = {
        label: field.field_title,
        formKey: field.field_name_id,
        type: field.field_type,
        placeholder: field.field_placeholder,
        required: field.field_required,
        field_option_source: field.field_option_source,
        field_options: field.field_options
      };

      if (
        [PRIOR_AUTH_ADDITIONAL_FIELDS.ADDITIONAL_INFO_NOTES].includes(
          inputField.formKey
        )
      ) {
        inputField.type = 'textarea'; // Change type to textarea
        inputField.isTextArea = true; // Add flag
      }

      // Hide defaultExcludedFields if their values are empty
      const fieldValue =
        ticketDetails?.prior_auth?.[
          inputField.formKey as keyof IPriorAuthTicketDetails['prior_auth']
        ];

      if (
        defaultExcludedFields.includes(inputField.formKey) &&
        (fieldValue === null || fieldValue === undefined || fieldValue === '')
      ) {
        return null; // Exclude the field if it’s empty
      }

      // TODO: HANDLE IT IN THE BACKEND
      // TODO: ADD TYPE SAFETY
      if (inputField.formKey === 'service_location_id') {
        inputField.itemList = serviceLocationOptionsList;
      }
      if (inputField.formKey === 'appointment_id') {
        inputField.itemList = patientAppointmentsOptionsList;
        inputField.disabled = !appointmentsEnabled;
      } else if (inputField.formKey === 'provider_id') {
        inputField.itemList = providersOptionList;
      } else if (inputField.formKey === 'payer_id') {
        inputField.fetchOptions = fetchPayersWithMatchingText;
        inputField.getOptionSelected = (option: IPayer, value: number) =>
          option.value === value;
        inputField.getOptionLabel = (value: number | IPayer) => {
          if (typeof value === 'object') {
            value = value?.value;
          }
          const payer = payerOptionsList.find(p => p.value === value);
          return payer
            ? payer.label
            : t('PRIOR_AUTHORIZATION.TICKET_DETAILS.SELECT_PAYER');
        };
        inputField.renderOption = (option: IPayer) => (
          <ListItemText primary={option.label} />
        );
      } else if (
        inputField.type === 'select' &&
        inputField.field_option_source === 'static'
      ) {
        // Parse field_options into an array of objects
        const optionsArray = JSON.parse(field.field_options);
        inputField.itemList = optionsArray ?? [];
      }
      if (inputField.type === 'select' && !inputField.required) {
        inputField.onClear = () => {
          methods.setValue(inputField.formKey, null);
        };
      }
      const excludedFields =
        excludedFieldsByStatus[workFlowStatusCode] ?? defaultExcludedFields;
      if (excludedFields.includes(inputField.formKey)) {
        return null; // Exclude the field if it matches any of the excluded fields for the current workflow status
      }

      // Disable specific fields based on workFlowStatusCode
      if (defaultExcludedFields.includes(inputField.formKey)) {
        inputField.disabled = true; // Disable the field
      }

      return inputField;
    })
    .filter(inputField => inputField !== null);
  const isGenerateProcedureEnabled = useIsGenerateProcedureEnabled();

  useEffect(() => {
    if (!isEditing) {
      // when appointment changes, check if the appointment has procedure code and payer_name. if exists update the corresponding input fields
      const selectedAppointment = fieldOptions.patientAppointmentsList.find(
        item => item.id === selectedAppointmentId
      );
      if (selectedAppointmentId && selectedAppointment) {
        // TODO: APPOINTMENT SHOULD HAVE PAYER ID NOT PAYER NAME IN THE API RES
        const defaultPayer = clinicPayersList.find(
          payer => payer.payer_name === selectedAppointment?.payer_name
        );
        if (defaultPayer) {
          methods.setValue('payer_id', defaultPayer.id);
        }
        // Update the procedure code mapping only if Generate CPT Code is not enabled at group level
        if (
          !isGenerateProcedureEnabled &&
          selectedAppointment?.procedure_code
        ) {
          methods.setValue(
            'procedure_code',
            selectedAppointment.procedure_code
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditing,
    selectedAppointmentId,
    fieldOptions.patientAppointmentsList,
    clinicPayersList
  ]);

  // When patient is selected, update the title to include the patient name.
  useEffect(() => {
    if (!isEditing) {
      let formattedPatientName = t(
        'PRIOR_AUTHORIZATION.TICKET_DETAILS.DEFAULT_PATIENT_NAME'
      );
      if (selectedPatient) {
        formattedPatientName = HelperUtils.formFullName(
          selectedPatient.first_name,
          selectedPatient.last_name,
          selectedPatient.middle_name
        );
      }
      methods.setValue(
        'title',
        t('PRIOR_AUTHORIZATION.TICKET_DETAILS.DEFAULT_TICKET_TITLE_VALUE', {
          patientName: formattedPatientName,
          date: HelperUtils.formatDisplayDate(new Date())
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient, isEditing]);

  const handleOpenCPTCodeGenerator = () => {
    setShowCPTCodeGenerator(true);
  };

  const handleSaveCPTCode = async (
    values: Parameters<IGenerateCPTCodeDialogProps['onSubmit']>[0]
  ) => {
    try {
      if (ticketDetails) {
        const payload: Parameters<typeof updateTicket>[0]['payload'] = {};
        payload.custom_fields = {};
        payload.custom_fields.prior_auth_id = ticketDetails.prior_auth?.id;
        payload.custom_fields.procedure_code = values.cptCode;
        payload.custom_fields.doctors_notes = values.doctorsNotes;

        await updateTicket({
          clinicGroupId,
          clinicId,
          ticketId: ticketDetails.id,
          payload,
          params: {
            module:
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER.PRIOR_AUTHORIZATION
          }
        });
        refreshTicketDetails();
        enqueueSnackbar('Updated Procedure Code', SnackBarConfig('s'));
      } else {
        methods.setValue('procedure_code', values.cptCode);
        methods.setValue('doctors_notes', values.doctorsNotes);
      }
    } catch (e) {
      if (e?.message) {
        enqueueSnackbar(e.message, SnackBarConfig('e'));
      }
    }
  };

  const getPriorAuthVisitType = () => {
    const ticketFieldDetails = methods.getValues();
    const priorAuthAppt = fieldOptions.patientAppointmentsList.find(
      item => item.id === ticketFieldDetails?.appointment_id
    );
    if (priorAuthAppt) return priorAuthAppt.type;
    return '';
  };

  return (
    <div className="prior-authorization-ticket-details-fields">
      {ticketInputFields.map(({formKey, ...inputItem}) => {
        return (
          <Controller
            key={formKey}
            control={methods.control}
            name={formKey}
            defaultValue={
              ticketDetails?.prior_auth?.[
                formKey as keyof IPriorAuthTicketDetails['prior_auth']
              ] ?? null
            }
            render={inputProps => {
              return (
                <div
                  style={{display: 'grid'}}
                  className={inputItem.isTextArea ? 'text-area-class' : ''}>
                  <FormField
                    {...inputItem}
                    {...inputProps}
                    onChange={(e: any, v: any) => {
                      if (formKey === 'payer_id') {
                        if (v) {
                          inputProps.onChange(v.value);
                        } else {
                          inputProps.onChange(null);
                        }
                      } else {
                        inputProps.onChange(e);
                      }
                      if (formKey === 'appointment_id') {
                        updateDateOfServiceOnAppointmentChanges(
                          e?.target?.value
                        );
                      }
                    }}
                    className={inputItem.disabled ? 'disabled-fields' : ''}
                    style={{
                      backgroundColor: inputItem.disabled && '#eeeeee', // Dark gray for disabled fields
                      flexGrow: inputItem.isTextArea ? '1' : '0' // Allow textarea to grow if needed
                    }}
                    error={methods.errors[formKey]}
                    multiline={inputItem.isTextArea} // Render textarea if flagged
                  />
                  {formKey === 'procedure_code' && hasSmartCoderPermission && (
                    <p
                      className="link-style"
                      onClick={handleOpenCPTCodeGenerator}>
                      {t('PRIOR_AUTHORIZATION.GENERATE_CPT_CODES')}
                    </p>
                  )}
                </div>
              );
            }}
          />
        );
      })}
      {/* prior-auth_id has to be passed to the update api, so adding it.*/}
      {ticketDetails && (
        <Controller
          control={methods.control}
          name="prior_auth_id"
          defaultValue={ticketDetails?.prior_auth?.id}
          render={() => <input type="hidden" />}
        />
      )}
      {!ticketDetails && (
        <Controller
          control={methods.control}
          name="doctors_notes"
          defaultValue={ticketDetails?.prior_auth?.id}
          render={() => <input type="hidden" />}
        />
      )}
      {showCPTCodeGenerator && (
        <GenerateCPTCodeDialog
          open={showCPTCodeGenerator}
          onClose={() => {
            setShowCPTCodeGenerator(false);
          }}
          onSubmit={handleSaveCPTCode}
          clinicGroupId={clinicGroupId}
          clinicId={clinicId}
          relatedType={SMART_CODER_RELATED_TYPES.PRIOR_AUTHORIZATIONS}
          relatedId={ticketDetails?.id}
          dialogHelperText={t(
            'PRIOR_AUTHORIZATION.GENERATE_CPT_CODE_DIALOG_HELPER_TEXT'
          )}
          showPriorAuthInfo={true}
          priorAuthTicketDetails={methods.getValues()}
          doctorsNotes={ticketDetails?.prior_auth?.doctors_notes ?? ""}
          visitType={getPriorAuthVisitType()}
        />
      )}
    </div>
  );
};

export default PriorAuthorizationTicketDetailsFields;
